/**
 * CSP-safe template renderer that doesn't use eval() or new Function()
 * Supports a subset of Nunjucks-like syntax:
 * - {{ variable }} - interpolation
 * - {{ object.property }} - dot notation
 * - {% if condition %}...{% endif %} - conditionals
 * - {% if condition %}...{% else %}...{% endif %} - if/else
 * - {% for item in array %}...{% endfor %} - loops
 */

type ContextValue = string | number | boolean | null | undefined | object | unknown[];

interface TemplateContext {
  [key: string]: ContextValue;
}

/**
 * Safely gets a nested property from an object using dot notation
 */
function getNestedValue(obj: TemplateContext, path: string): ContextValue {
  const parts = path.trim().split('.');
  let current: unknown = obj;
  
  for (const part of parts) {
    if (current === null || current === undefined) {
      return undefined;
    }
    if (typeof current === 'object' && current !== null) {
      current = (current as Record<string, unknown>)[part];
    } else {
      return undefined;
    }
  }
  
  return current as ContextValue;
}

/**
 * Evaluates a simple condition expression without using eval()
 * Supports: variable, variable === 'string', variable !== 'string', 
 * variable === number, !variable, variable.length, variable.length > 0
 */
function evaluateCondition(condition: string, context: TemplateContext): boolean {
  const trimmed = condition.trim();
  
  // Handle negation: !variable
  if (trimmed.startsWith('!')) {
    const varName = trimmed.slice(1).trim();
    const value = getNestedValue(context, varName);
    return !value;
  }
  
  // Handle strict equality: variable === 'value' or variable === "value"
  const strictEqMatch = trimmed.match(/^(.+?)\s*===\s*(['"])(.*)(['"])$/);
  if (strictEqMatch) {
    const [, varName, , expectedValue] = strictEqMatch;
    const actualValue = getNestedValue(context, varName.trim());
    return actualValue === expectedValue;
  }
  
  // Handle strict inequality: variable !== 'value'
  const strictNeqMatch = trimmed.match(/^(.+?)\s*!==\s*(['"])(.*)(['"])$/);
  if (strictNeqMatch) {
    const [, varName, , expectedValue] = strictNeqMatch;
    const actualValue = getNestedValue(context, varName.trim());
    return actualValue !== expectedValue;
  }
  
  // Handle numeric comparisons: variable > 0, variable.length > 0, etc.
  const numCompareMatch = trimmed.match(/^(.+?)\s*(>|<|>=|<=|===|!==)\s*(\d+)$/);
  if (numCompareMatch) {
    const [, varName, operator, numStr] = numCompareMatch;
    const actualValue = getNestedValue(context, varName.trim());
    const num = Number(numStr);
    const actualNum = typeof actualValue === 'number' ? actualValue : 
                      (Array.isArray(actualValue) ? actualValue.length : 0);
    
    switch (operator) {
      case '>': return actualNum > num;
      case '<': return actualNum < num;
      case '>=': return actualNum >= num;
      case '<=': return actualNum <= num;
      case '===': return actualNum === num;
      case '!==': return actualNum !== num;
    }
  }
  
  // Handle 'and' operator: condition1 and condition2
  if (trimmed.includes(' and ')) {
    const parts = trimmed.split(' and ');
    return parts.every(part => evaluateCondition(part, context));
  }
  
  // Handle 'or' operator: condition1 or condition2
  if (trimmed.includes(' or ')) {
    const parts = trimmed.split(' or ');
    return parts.some(part => evaluateCondition(part, context));
  }
  
  // Simple variable truthiness check
  const value = getNestedValue(context, trimmed);
  
  // Arrays are truthy if they have length
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  
  return Boolean(value);
}

/**
 * Processes a single interpolation: {{ expression }}
 */
function processInterpolation(expression: string, context: TemplateContext): string {
  const trimmed = expression.trim();
  
  // Handle filters (e.g., {{ value or 'default' }})
  if (trimmed.includes(' or ')) {
    const [varPart, defaultPart] = trimmed.split(' or ');
    const value = getNestedValue(context, varPart.trim());
    if (value !== null && value !== undefined && value !== '') {
      return String(value);
    }
    // Parse default value (remove quotes if present)
    const defaultValue = defaultPart.trim().replace(/^['"]|['"]$/g, '');
    return defaultValue;
  }
  
  const value = getNestedValue(context, trimmed);
  
  if (value === null || value === undefined) {
    return '';
  }
  
  return String(value);
}

interface ParsedBlock {
  type: 'text' | 'if' | 'for' | 'interpolation';
  content: string;
  condition?: string;
  variable?: string;
  iterable?: string;
  elseContent?: string;
}

/**
 * Parses the template into blocks for processing
 */
function parseTemplate(template: string): ParsedBlock[] {
  const blocks: ParsedBlock[] = [];
  let remaining = template;
  
  while (remaining.length > 0) {
    // Find the next tag or interpolation
    const ifMatch = remaining.match(/\{%\s*if\s+(.+?)\s*%\}/);
    const forMatch = remaining.match(/\{%\s*for\s+(\w+)\s+in\s+(.+?)\s*%\}/);
    const interpMatch = remaining.match(/\{\{(.+?)\}\}/);
    
    // Find which comes first
    const positions = [
      ifMatch ? remaining.indexOf(ifMatch[0]) : Infinity,
      forMatch ? remaining.indexOf(forMatch[0]) : Infinity,
      interpMatch ? remaining.indexOf(interpMatch[0]) : Infinity,
    ];
    
    const minPos = Math.min(...positions);
    
    if (minPos === Infinity) {
      // No more tags, rest is plain text
      if (remaining) {
        blocks.push({ type: 'text', content: remaining });
      }
      break;
    }
    
    // Add any text before the match
    if (minPos > 0) {
      blocks.push({ type: 'text', content: remaining.slice(0, minPos) });
    }
    
    if (positions[0] === minPos && ifMatch) {
      // Process if block
      const condition = ifMatch[1];
      const afterIf = remaining.slice(minPos + ifMatch[0].length);
      
      // Find matching endif (accounting for nested ifs)
      let depth = 1;
      let pos = 0;
      let elsePos = -1;
      
      while (depth > 0 && pos < afterIf.length) {
        const nextIf = afterIf.slice(pos).match(/\{%\s*if\s+/);
        const nextEndif = afterIf.slice(pos).match(/\{%\s*endif\s*%\}/);
        const nextElse = afterIf.slice(pos).match(/\{%\s*else\s*%\}/);
        
        const nextIfPos = nextIf ? pos + afterIf.slice(pos).indexOf(nextIf[0]) : Infinity;
        const nextEndifPos = nextEndif ? pos + afterIf.slice(pos).indexOf(nextEndif[0]) : Infinity;
        const nextElsePos = nextElse ? pos + afterIf.slice(pos).indexOf(nextElse[0]) : Infinity;
        
        const nextPos = Math.min(nextIfPos, nextEndifPos, nextElsePos);
        
        if (nextPos === Infinity) break;
        
        if (nextPos === nextIfPos) {
          depth++;
          pos = nextIfPos + 1;
        } else if (nextPos === nextEndifPos) {
          depth--;
          if (depth === 0) {
            const endifMatch = afterIf.slice(nextEndifPos).match(/\{%\s*endif\s*%\}/);
            if (endifMatch) {
              let content = afterIf.slice(0, nextEndifPos);
              let elseContent: string | undefined;
              
              if (elsePos !== -1 && elsePos < nextEndifPos) {
                const elseMatch = afterIf.slice(elsePos).match(/\{%\s*else\s*%\}/);
                if (elseMatch) {
                  content = afterIf.slice(0, elsePos);
                  elseContent = afterIf.slice(elsePos + elseMatch[0].length, nextEndifPos);
                }
              }
              
              blocks.push({ 
                type: 'if', 
                content, 
                condition,
                elseContent 
              });
              remaining = afterIf.slice(nextEndifPos + endifMatch[0].length);
            }
          } else {
            pos = nextEndifPos + 1;
          }
        } else if (nextPos === nextElsePos && depth === 1) {
          elsePos = nextElsePos;
          pos = nextElsePos + 1;
        } else {
          pos = nextPos + 1;
        }
      }
      
      if (depth > 0) {
        // Unclosed if, treat as text
        blocks.push({ type: 'text', content: ifMatch[0] });
        remaining = afterIf;
      }
    } else if (positions[1] === minPos && forMatch) {
      // Process for block
      const variable = forMatch[1];
      const iterable = forMatch[2];
      const afterFor = remaining.slice(minPos + forMatch[0].length);
      
      // Find matching endfor (accounting for nested fors)
      let depth = 1;
      let pos = 0;
      
      while (depth > 0 && pos < afterFor.length) {
        const nextFor = afterFor.slice(pos).match(/\{%\s*for\s+/);
        const nextEndfor = afterFor.slice(pos).match(/\{%\s*endfor\s*%\}/);
        
        const nextForPos = nextFor ? pos + afterFor.slice(pos).indexOf(nextFor[0]) : Infinity;
        const nextEndforPos = nextEndfor ? pos + afterFor.slice(pos).indexOf(nextEndfor[0]) : Infinity;
        
        if (nextForPos < nextEndforPos) {
          depth++;
          pos = nextForPos + 1;
        } else if (nextEndforPos < Infinity) {
          depth--;
          if (depth === 0) {
            const endforMatch = afterFor.slice(nextEndforPos).match(/\{%\s*endfor\s*%\}/);
            if (endforMatch) {
              blocks.push({ 
                type: 'for', 
                content: afterFor.slice(0, nextEndforPos), 
                variable,
                iterable 
              });
              remaining = afterFor.slice(nextEndforPos + endforMatch[0].length);
            }
          } else {
            pos = nextEndforPos + 1;
          }
        } else {
          break;
        }
      }
      
      if (depth > 0) {
        // Unclosed for, treat as text
        blocks.push({ type: 'text', content: forMatch[0] });
        remaining = afterFor;
      }
    } else if (interpMatch) {
      // Process interpolation
      blocks.push({ type: 'interpolation', content: interpMatch[1] });
      remaining = remaining.slice(minPos + interpMatch[0].length);
    }
  }
  
  return blocks;
}

/**
 * Renders parsed blocks with the given context
 */
function renderBlocks(blocks: ParsedBlock[], context: TemplateContext): string {
  let result = '';
  
  for (const block of blocks) {
    switch (block.type) {
      case 'text':
        result += block.content;
        break;
        
      case 'interpolation':
        result += processInterpolation(block.content, context);
        break;
        
      case 'if': {
        const conditionMet = evaluateCondition(block.condition!, context);
        if (conditionMet) {
          result += renderTemplate(block.content, context);
        } else if (block.elseContent !== undefined) {
          result += renderTemplate(block.elseContent, context);
        }
        break;
      }
        
      case 'for': {
        const iterableValue = getNestedValue(context, block.iterable!);
        if (Array.isArray(iterableValue)) {
          for (const item of iterableValue) {
            const loopContext = { 
              ...context, 
              [block.variable!]: item 
            };
            result += renderTemplate(block.content, loopContext);
          }
        }
        break;
      }
    }
  }
  
  return result;
}

/**
 * Main function to render a template with the given context
 * This is a CSP-safe alternative to Nunjucks
 */
export function renderTemplate(template: string, context: TemplateContext): string {
  const blocks = parseTemplate(template);
  return renderBlocks(blocks, context);
}
